import {
  keepPreviousData,
  useQuery,
  type UseQueryResult,
} from "@tanstack/react-query";

import { useDomainServices } from "@/providers/DomainServices";
import type { SearchQueryBody } from "@/types";

import {
  type QueryContentMode,
  type QueryRecord,
  type QueryTarget,
} from "../QueryRecord";

interface UseStructuredQueryOptions {
  resolvedOrgId: string | undefined;
  /**
   * The full query body, including any `max_results` cap. Callers bake the cap
   * into the body (e.g. via `composeQueryBody({ maxResults })`); the hook submits
   * the body verbatim.
   */
  query: SearchQueryBody;
  target: QueryTarget;
  contentMode: QueryContentMode;
  /**
   * Caller-supplied cache key (e.g., `datasetQueryKeys.list(orgId, query)`)
   * so submit cache aligns with mutation-side invalidation.
   */
  queryKey: readonly unknown[];
}

export function useStructuredQuery(
  opts: UseStructuredQueryOptions,
): UseQueryResult<QueryRecord, Error> {
  const { queryService } = useDomainServices();
  const { resolvedOrgId, query, target, contentMode, queryKey } = opts;

  return useQuery({
    queryKey,
    queryFn: ({ signal }) =>
      queryService.submitStructured(
        {
          query,
          target,
          content_mode: contentMode,
        },
        { resourceOwnerId: resolvedOrgId, abortSignal: signal },
      ),
    enabled: !!resolvedOrgId,
    placeholderData: keepPreviousData,
  });
}
